import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useService } from '../../services/services';
import Button from '../../components/form/elements/button';
import Link from '../../components/form/elements/link';
import { route } from '../../services/utils';
import { TOKENS_INDEX, TOKENS_VIEW } from '../../router/routes';
import Form, { SubmitType } from '../../components/form';
import Token from '../../interfaces/token';
import * as yup from 'yup';
import Group from '../../components/form/elements/group';
import Text from '../../components/form/elements/text';
import OptionModal from '../../components/modals/option-modal';
import { useHistory } from 'react-router';
import css from './index.module.css';

interface TokenServiceStoreResponseProps {
  plainTextToken: string;
  accessToken: Token;
}

export default function ApiTokenCreateCustom({ name }: { name?: string }) {
  const history = useHistory();
  const { tokenService } = useService();
  const { t } = useTranslation();
  const elemRef = useRef(null);
  const [values] = useState(() => ({ name }));

  const [body] = useState(() => [Group([Text('name')])]);
  const [footer] = useState(() => [
    Button('create'),
    Link(route(TOKENS_INDEX), 'cancel'),
  ]);

  const [modal, setModal] = useState(null);
  const onModalClose = useCallback(
    (id) => {
      history.push(route(TOKENS_VIEW, { id }));
    },
    [history]
  );

  const copyTextToClipboard = useCallback(() => {
    elemRef.current.select();
    document.execCommand('copy');
  }, [elemRef]);

  const blur = useCallback(() => {
    const tmp: HTMLInputElement = document.createElement('input');
    document.body.appendChild(tmp);
    tmp.focus();
    document.body.removeChild(tmp);
  }, []);

  const showSuccessModal = useCallback(
    (plainTextToken: string, accessToken: Token) => {
      setModal(
        <OptionModal
          title={t('pages.tokens.create.token-created')}
          content={<p>{t('pages.tokens.create.token-copied')}</p>}
          buttons={[{ title: t('common.close'), name: 'agreed', value: false }]}
          onChoice={() => onModalClose(accessToken.id)}
          onClose={() => onModalClose(accessToken.id)}
          shouldCloseOnOverlayClick={false}
        />
      );
    },
    [t, onModalClose, setModal]
  );

  const showTokenModal = useCallback(
    ({ plainTextToken, accessToken }) => {
      setModal(
        <OptionModal
          title={t('pages.tokens.create.token-created')}
          content={
            <>
              <p>{t('pages.tokens.create.save-token')}</p>
              <textarea
                value={plainTextToken}
                readOnly
                ref={elemRef}
                className={css['created-token']}
              />
            </>
          }
          buttons={[
            { title: t('copy'), name: 'agreed', value: true },
            { title: t('common.close'), name: 'agreed', value: false },
          ]}
          onChoice={(value) => {
            if (value) {
              copyTextToClipboard();
              blur();
              showSuccessModal(plainTextToken, accessToken);
            } else {
              onModalClose(accessToken.id);
            }
          }}
          onClose={() => onModalClose(accessToken.id)}
          shouldCloseOnOverlayClick={false}
        />
      );
    },
    [t, copyTextToClipboard, blur, onModalClose, setModal, showSuccessModal]
  );

  const submit = useCallback<SubmitType<Token, TokenServiceStoreResponseProps>>(
    (form) => [
      tokenService.store(form),
      (res: TokenServiceStoreResponseProps) => showTokenModal(res),
    ],
    [tokenService, showTokenModal]
  );

  return (
    <>
      {modal}

      <Form
        title={'pages.tokens.create-token'}
        backTo={route(TOKENS_INDEX)}
        mode="create"
        body={body}
        footer={footer}
        values={values}
        schema={tokenSchema}
        submit={submit}
      />
    </>
  );
}

const tokenSchema = yup.object().shape({
  name: yup.string().required(),
});
